import hangupSound from '@/assets/ringtones/hang-up.mp3';
import startSound from '@/assets/ringtones/start.mp3';
import { MicrophonePermissionModal } from '@/components/microphone-permissions/microphone-permission-modal';
import { useNumberConfig } from '@/hooks/api/use-number-config-query';
import { useCallPictureInPicture } from '@/hooks/calls/use-call-picture-in-picture';
import { useNotificationPermission } from '@/hooks/notifications/use-notification';
import { useMicrophonePermission } from '@/hooks/permissions/use-microphone-permission';
import { useTranslation } from '@/hooks/use-translation';
import { parseWhisper } from '@/lib/phone-number';
import { useAudioDevices } from '@/providers/audio-devices/audio-devices-provider';
import type { paths } from '@/services/api-allo/api-allo-v1';
import { $apiAllo, $fetchAllo } from '@/services/api-allo/client';
import { createLogger } from '@/services/logger/logger';
import { nativeApi } from '@/services/native-api/native-api';
import { tracingService } from '@/services/tracing/tracing-service';
import { CallEventLevel, CallEventName } from '@/services/tracing/types';
import type { Line, PhoneNumberData } from '@/types/calls-types';
import type { HubSpotOutboundCallEvent } from '@/types/hubspot';
import { useActivePowerDialerSafe } from '@/views/power-dialer/providers/active-power-dialer-provider';
import { useVoiceCallActions } from '@packages/client-sdk-react/src/voice/use-voice-call-actions';
import {
  type VoiceCallContext,
  type VoiceCallStateStatus,
  type VoiceContact,
  type VoiceContactExtra,
  type VoiceContactUserLine,
  useVoiceCallState,
} from '@packages/client-sdk-react/src/voice/voice-call-container';
import { useVonageClient } from '@packages/client-sdk-react/src/vonage-client-provider';
import * as Sentry from '@sentry/react';
import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { HangupReason, type Json } from '@vonage/client-sdk';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLines } from '../lines/use-lines';
import { useCallHealthMonitor } from './hooks/use-call-health-monitor';
import { useVerifiedHangup } from './hooks/use-verified-hangup';

const vonageMediaLog = createLogger('AppCallActions', ['calls', 'vonage']);

export type DeclineState =
  | { state: 'voicemail' }
  | { state: 'ai-receptionist' }
  | { state: 'call-transfer'; extra: string }
  | { state: 'cascade' }
  | { state: 'unknown' };

interface AppCallActionsContextType {
  invite: { from?: string; channel?: string };
  answerCall: () => Promise<boolean>;
  rejectCall: () => Promise<void>;
  hangupCall: () => Promise<void>;
  toggleMute: () => Promise<void>;
  toggleEarmuff: () => Promise<void>;
  sendDtmf: (digit: string) => void;
  transferCall: () => void;
  transferCallFailed: () => void;
  call: (
    currentLine: Line,
    phoneNumber: PhoneNumberData,
    hubspotCallEvent?: HubSpotOutboundCallEvent,
    contactId?: string,
    context?: Json,
    options?: { signal?: AbortSignal },
  ) => Promise<boolean>;
  updateContact: (
    contact?: VoiceContact,
    extra?: VoiceContactExtra,
    userLine?: VoiceContactUserLine,
  ) => void;
  contact?: VoiceContact;
  callContext?: VoiceCallContext;
  userLine?: VoiceContactUserLine;
  callStatus: VoiceCallStateStatus;
  callId?: string;
  from?: { name: string; channel: string };
  direction: 'incoming' | 'outgoing' | 'unknown';
  isMuted: boolean;
  isEarmuffed: boolean;
  isRingingInbound: boolean;
  declineState: DeclineState;
  canNavigateToCall: boolean;
  navigateToCall: (overrides?: {
    phoneNumber?: string;
    contactId?: string;
    lineNumber?: string;
  }) => Promise<void>;
  isNavigatingToCall: boolean;
  openPipWindow: () => Promise<void>;
  hidePipWindow: () => void;
  isPipWindowOpen: boolean;
  duration: number;
}

export const AppCallActionsContext = createContext<AppCallActionsContextType | null>(null);

export const AppCallActionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const whisperRequest = useRef<{
    request: () => Promise<void>;
    abortController: AbortController;
  } | null>(null);
  const callIdRef = useRef<string | undefined>(undefined);
  const [duration, setDuration] = useState(0);
  const wasRingingRef = useRef(false);
  const [isNavigatingToCall, setIsNavigatingToCall] = useState(false);

  const previousCallStateRef = useRef<VoiceCallStateStatus | undefined>(undefined);
  const activeCallContactRef = useRef<string | undefined>(undefined);

  const voiceActions = useVoiceCallActions();
  const vonageClient = useVonageClient();
  const { endCall: verifiedEndCall } = useVerifiedHangup();
  useCallHealthMonitor();
  const { t } = useTranslation('desktop_calls');
  const [callState, callDispatch] = useVoiceCallState();
  const { pushNotification } = useNotificationPermission();

  const { lines, currentLine, setCurrentLine } = useLines();
  const navigate = useNavigate();
  const { pathname, searchStr } = useLocation();
  const { selectedMicrophone } = useAudioDevices();
  const { hasMicrophonePermission } = useMicrophonePermission();
  const [showMicPermissionModal, setShowMicPermissionModal] = useState(false);
  const { data: numberConfigData } = useNumberConfig();

  const declineState = useMemo((): DeclineState => {
    const config = numberConfigData?.data;
    if (!config) {
      return { state: 'unknown' };
    }

    if (config.ringing_mode === 'CASCADE') {
      return { state: 'cascade' };
    }

    switch (config.assistant_type) {
      case 'BLAND':
      case 'ELEVEN_LABS':
        return { state: 'ai-receptionist' };
      case 'EXTERNAL_PHONE':
        return { state: 'call-transfer', extra: config.assistant_transfer_number ?? '' };
      case 'NONE':
        return { state: 'voicemail' };
      default:
        return { state: 'unknown' };
    }
  }, [numberConfigData?.data]);

  const queryClient = useQueryClient();

  useEffect(() => {
    voiceActions.setSessionErrorRecoveryHandler(async () => {
      await verifiedEndCall({ source: 'session_error' });
    });

    return () => {
      voiceActions.setSessionErrorRecoveryHandler(null);
    };
  }, [voiceActions.setSessionErrorRecoveryHandler, verifiedEndCall]);

  useEffect(() => {
    const currentCallId = voiceActions.callId;
    if (currentCallId !== callIdRef.current) {
      setDuration(0);
      callIdRef.current = currentCallId;
    }
  }, [voiceActions.callId]);

  useEffect(() => {
    if (voiceActions.callStatus !== 'in-progress') {
      return;
    }

    if (voiceActions.contact?.phoneNumber) {
      activeCallContactRef.current = voiceActions.contact.phoneNumber;
    }

    const interval = setInterval(() => {
      setDuration((prev) => prev + 1);
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [voiceActions.callStatus, voiceActions.contact?.phoneNumber]);

  const invalidateCallQueries = useCallback(() => {
    queryClient.invalidateQueries($apiAllo.queryOptions('get', '/v1/call'));
    queryClient.invalidateQueries($apiAllo.queryOptions('get', '/v2/conversations'));
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === 'get' &&
        typeof query.queryKey[1] === 'string' &&
        query.queryKey[1].startsWith('/v2/conversations/') &&
        query.queryKey[1] !== '/v2/conversations/metrics',
    });
    queryClient.invalidateQueries({ queryKey: ['get', '/v1/conversations/metrics'] });
  }, [queryClient]);

  // Play hang-up sound when the remote party ends the call
  // and refetch conversation data when a call ends (any reason)
  useEffect(() => {
    const previousCallState = previousCallStateRef.current;
    previousCallStateRef.current = voiceActions.callStatus;

    // Check if the call just transitioned to idle from an active state
    const wasInActiveCall =
      previousCallState === 'in-progress' ||
      previousCallState === 'ringing' ||
      previousCallState === 'reconnectable' ||
      previousCallState === 'hangingup' ||
      previousCallState === 'initiating' ||
      previousCallState === 'transferring';

    if (
      wasInActiveCall &&
      voiceActions.callStatus === 'idle' &&
      callState.reason === HangupReason.RemoteHangup
    ) {
      const audio = new Audio(hangupSound);
      audio.play().catch(() => {
        // Ignore audio play errors (e.g., autoplay policy)
      });
    }

    // Optimistically update sidebar and refetch when a call ends (any reason).
    // The post-call "Go to conversation / Rate call" toast lives in
    // CallEndedToastListener, which is mounted inside ActivePowerDialerProvider
    // so it can actually read the PD session state (this provider is an
    // ancestor of ActivePowerDialerProvider, so reading PD context from here
    // would always yield null).
    if (wasInActiveCall && voiceActions.callStatus === 'idle') {
      const contactNumber = activeCallContactRef.current;
      activeCallContactRef.current = undefined;

      // Flip ONGOING_CALL → CALL in all conversation list caches so the
      // sidebar updates instantly instead of waiting for the refetch

      if (contactNumber) {
        type ConversationsResponse =
          paths['/v2/conversations']['get']['responses']['200']['content']['application/json'];

        const toDigits = (s: string) => s.replace(/\D/g, '');
        const contactDigits = toDigits(contactNumber);

        const queries = queryClient
          .getQueryCache()
          .findAll({ queryKey: ['get', '/v2/conversations'] });

        for (const query of queries) {
          queryClient.setQueryData<InfiniteData<ConversationsResponse>>(
            query.queryKey,
            (oldData) => {
              if (!oldData?.pages) {
                return oldData;
              }

              let changed = false;
              const newPages = oldData.pages.map((page) => ({
                ...page,
                data: page.data?.map((conv) => {
                  if (
                    conv.number &&
                    toDigits(conv.number) === contactDigits &&
                    conv.last_item?.type === 'ONGOING_CALL'
                  ) {
                    changed = true;
                    return {
                      ...conv,
                      last_item: {
                        ...conv.last_item,
                        type: 'CALL' as const,
                        body: {
                          ...conv.last_item?.body,
                          summary: undefined,
                          one_sentence_summary: undefined,
                        },
                      },
                    };
                  }
                  return conv;
                }),
              }));

              return changed ? { ...oldData, pages: newPages } : oldData;
            },
          );
        }
      }

      // Only invalidate call history and metrics — skip /v2/conversations
      // to avoid the refetch overwriting the optimistic update above
      // (backend may still return ONGOING_CALL at this point).
      // The WS CALL_UPDATED event or the hangup callback's delayed
      // invalidation will refresh conversations with real data later.
      queryClient.invalidateQueries($apiAllo.queryOptions('get', '/v1/call'));
      queryClient.invalidateQueries({ queryKey: ['get', '/v1/conversations/metrics'] });
    }
  }, [voiceActions.callStatus, callState.reason, queryClient]);

  const handleSecondCall = useCallback(async (): Promise<boolean> => {
    // End the first call with verified hangup
    if (callState.callId) {
      const terminated = await verifiedEndCall({ source: 'second_call' });
      if (!terminated) {
        // First call couldn't be verified as ended — don't answer second
        return false;
      }
    }

    // Answer the second call
    if (callState.secondCall?.id) {
      tracingService.trackEvent(CallEventName.AttemptAnswer, CallEventLevel.Info, {
        deviceLegId: callState.secondCall.id,
        direction: 'inbound',
      });
      try {
        await vonageClient.answer(callState.secondCall.id);
        callDispatch({ type: 'call:second:answered' });
        return true;
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: 'app_call_actions', action: 'answer_second_call' },
        });
        return false;
      }
    }

    return false;
  }, [callState.callId, callState.secondCall?.id, vonageClient, callDispatch, verifiedEndCall]);

  const answerCall = useCallback(async (): Promise<boolean> => {
    if (hasMicrophonePermission !== 'granted') {
      setShowMicPermissionModal(true);
      return false;
    }

    try {
      let success: boolean;

      if (callState.hasSecondIncomingCall) {
        success = await handleSecondCall();
      } else {
        success = await voiceActions.answerCall();
      }

      invalidateCallQueries();

      return success;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'app_call_actions', action: 'answer_call' },
      });
      // Still invalidate queries even if answer fails
      invalidateCallQueries();
      return false;
    }
  }, [
    hasMicrophonePermission,
    voiceActions.answerCall,
    callState.hasSecondIncomingCall,
    handleSecondCall,
    invalidateCallQueries,
  ]);

  const rejectCall = useCallback(async () => {
    try {
      if (callState.hasSecondIncomingCall && callState.secondCall?.id) {
        const secondCallId = callState.secondCall.id;
        await vonageClient.reject(callState.secondCall.id).finally(() => {
          callDispatch({ type: 'call:invite:rejected', rejectedCallId: secondCallId });
        });
      } else {
        await voiceActions.rejectCall();
      }

      invalidateCallQueries();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'app_call_actions', action: 'reject_call' },
      });
      // Still invalidate queries even if reject fails
      invalidateCallQueries();
    }
  }, [
    voiceActions.rejectCall,
    callState.hasSecondIncomingCall,
    callState.secondCall?.id,
    vonageClient,
    callDispatch,
    invalidateCallQueries,
  ]);

  const handleWhisperRequest = useCallback(
    // NOTE: name here is actually number without the +
    async (name: string, notification: Notification | undefined) => {
      // name is number without +
      const phoneNumber = `+${name}`;

      try {
        // TODO: handle the summary property and display on incoming dialog
        const { data } = await $fetchAllo.GET('/v1/call/{number}/whisper', {
          params: {
            path: { number: name },
            query: {
              no_image: true,
              is_outbound: false,
              ...(callState.callId ? { call_id: callState.callId } : {}),
            },
          },
          signal: whisperRequest.current?.abortController.signal,
        });

        if (data?.data) {
          const contact = parseWhisper(data.data, phoneNumber);
          notification?.close();

          if (!nativeApi) {
            pushNotification(t('calls.inbound.notification.title', { name: contact.contact.name }));
          }

          if (callState.callState === 'in-progress' || callState.callState === 'ringing') {
            callDispatch({
              type: 'call:second:contact:updated',
              contact: contact.contact,
              extra: contact.extra,
              userLine: contact.userLine,
            });

            if (nativeApi) {
              pushNotification(
                t('calls.inbound.notification.title', {
                  name: contact.contact.name ?? t('calls.unknown', { ns: 'desktop_calls' }),
                }),
              );
            }
          } else {
            voiceActions.updateContact(contact.contact, contact.extra, contact.userLine);
          }
        }
      } catch (error) {
        // Don't block the call flow - the call can proceed without whisper data
        if (error instanceof Error && error.name !== 'AbortError') {
          Sentry.captureException(error, {
            tags: { component: 'app_call_actions', action: 'whisper_request' },
          });
        }
      }
    },
    [
      callState.callState,
      callState.callId,
      callDispatch,
      pushNotification,
      t,
      voiceActions.updateContact,
    ],
  );

  useEffect(() => {
    const onCallInvite = vonageClient.on('callInvite', async (_callId, name, _channel) => {
      try {
        let notification: undefined | Notification;

        // name is number without +
        const phoneNumber = `+${name}`;

        if (!nativeApi) {
          notification = await pushNotification(
            t('calls.inbound.notification.title', { name: phoneNumber }),
          );
        }

        if (whisperRequest.current) {
          whisperRequest.current.abortController.abort();
        }

        const abortController = new AbortController();
        whisperRequest.current = {
          request: () => handleWhisperRequest(name, notification),
          abortController,
        };

        whisperRequest.current.request();
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: 'app_call_actions', action: 'call_invite' },
        });
      }
    });

    return () => {
      vonageClient.off('callInvite', onCallInvite);
    };
  }, [vonageClient, handleWhisperRequest, pushNotification, t, callState.callId]);

  // Surface Vonage media errors to New Relic + the backend tracing stream,
  // correlated to the call by callId.
  useEffect(() => {
    const handleCallMediaError = vonageClient.on('callMediaError', (callId, error) => {
      const errorData = {
        errorType: error.type,
        errorCode: error.code ?? undefined,
        errorName: error.name,
        errorMessage: error.message,
        errorCause: error.kmpCause?.message,
      };

      vonageMediaLog.error('Vonage media error', { callId, ...errorData });
      tracingService.trackEvent(CallEventName.VonageMediaError, CallEventLevel.Error, {
        deviceLegId: callId,
        ...errorData,
      });
    });

    return () => {
      vonageClient.off('callMediaError', handleCallMediaError);
    };
  }, [vonageClient]);

  const isRingingInbound = useMemo(() => {
    return (
      (voiceActions.isRinging && voiceActions.direction === 'incoming') ||
      callState.hasSecondIncomingCall
    );
  }, [voiceActions.isRinging, voiceActions.direction, callState.hasSecondIncomingCall]);

  useEffect(() => {
    if (!nativeApi) {
      return;
    }

    const isCurrentlyRinging = isRingingInbound;

    // Only hide if we were previously ringing and now we're not
    if (wasRingingRef.current && !isCurrentlyRinging) {
      nativeApi.incomingCall.hide.callback();
    }

    // Track the ringing state for next render
    wasRingingRef.current = isCurrentlyRinging;
  }, [isRingingInbound]);

  useEffect(() => {
    if (isRingingInbound && hasMicrophonePermission !== 'granted') {
      setShowMicPermissionModal(true);
      return;
    }

    setShowMicPermissionModal(false);
  }, [isRingingInbound, hasMicrophonePermission]);

  const call = useCallback(
    async (
      currentLine: Line,
      phoneNumber: PhoneNumberData,
      hubspotCallEvent?: HubSpotOutboundCallEvent,
      contactId?: string,
      context?: Json,
      options?: { signal?: AbortSignal },
    ) => {
      if (options?.signal?.aborted) {
        return false;
      }

      if (hasMicrophonePermission !== 'granted') {
        setShowMicPermissionModal(true);
        return false;
      }

      try {
        if (options?.signal?.aborted) {
          return false;
        }
        // Play start tone when initiating outbound call
        const audio = new Audio(startSound);
        audio.play().catch(() => {
          // Ignore audio play errors (e.g., autoplay policy)
        });

        const started = await voiceActions.startCall(
          {
            to: phoneNumber.number.number,
            origin_number: currentLine.phoneNumber.number.number,
          },
          {
            id: contactId,
            name: phoneNumber.user?.name,
            phoneNumber: phoneNumber.number.number,
          },
          context,
          hubspotCallEvent,
          options,
        );

        invalidateCallQueries();
        return started;
      } catch (error) {
        // Still invalidate queries even if call start fails
        invalidateCallQueries();
        Sentry.captureException(error, {
          tags: { component: 'app_call_actions', action: 'start_call' },
        });
        return false;
      }
    },
    [voiceActions.startCall, invalidateCallQueries, hasMicrophonePermission],
  );

  const hangupCall = useCallback(async () => {
    // Play hangup sound immediately for instant user feedback —
    // the sound signals "we sent the hangup", not "the hangup is confirmed"
    const audio = new Audio(hangupSound);
    audio.play().catch(() => {
      // Ignore audio play errors (e.g., autoplay policy)
    });

    try {
      await verifiedEndCall({ source: 'user' });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'app_call_actions', action: 'hangup_call' },
      });
    }
  }, [verifiedEndCall]);

  const sendDtmf = useCallback(
    (digit: string) => {
      if (voiceActions.callStatus !== 'in-progress') {
        return;
      }
      voiceActions.sendDtmf(digit);
    },
    [voiceActions.callStatus, voiceActions.sendDtmf],
  );

  const defaultUserLine = useMemo(
    () => ({
      lineName: currentLine.lineName,
      lineNumber: currentLine.phoneNumber.number.format('E.164'),
    }),
    [currentLine.lineName, currentLine.phoneNumber],
  );

  const handleNavigateToCall = useCallback(
    async (overrides?: {
      phoneNumber?: string;
      contactId?: string;
      lineNumber?: string;
    }) => {
      const contactPhoneNumber = overrides?.phoneNumber ?? voiceActions.contact?.phoneNumber;
      const contactId = overrides?.contactId ?? voiceActions.contact?.id;
      const callLineNumber =
        overrides?.lineNumber ??
        callState.userLine?.lineNumber ??
        voiceActions.callContext?.origin_number;

      if (callLineNumber) {
        const matchingLine = lines.find(
          (l) => l.phoneNumber.number.format('E.164') === callLineNumber,
        );
        if (matchingLine && matchingLine !== currentLine) {
          setIsNavigatingToCall(true);
          setCurrentLine(matchingLine);
        }
      }

      await navigate({
        to: '/',
        search: {
          ...(contactId ? { contactId, openPanel: true } : {}),
          ...(contactPhoneNumber ? { number: contactPhoneNumber } : {}),
        },
      });
      setIsNavigatingToCall(false);
    },
    [
      navigate,
      voiceActions.contact?.id,
      voiceActions.contact?.phoneNumber,
      callState.userLine?.lineNumber,
      voiceActions.callContext?.origin_number,
      lines,
      currentLine,
      setCurrentLine,
    ],
  );

  const canNavigateToCall = useMemo(() => {
    const contactPhoneNumber = voiceActions.contact?.phoneNumber;
    const contactId = voiceActions.contact?.id;
    const callLineNumber =
      callState.userLine?.lineNumber ?? voiceActions.callContext?.origin_number;

    if (!contactPhoneNumber) {
      return false;
    }

    const isWrongLine =
      !!callLineNumber && currentLine.phoneNumber.number.format('E.164') !== callLineNumber;

    if (isWrongLine || pathname !== '/') {
      return true;
    }

    const searchParams = new URLSearchParams(searchStr);
    if (contactId) {
      return searchParams.get('contactId') !== contactId;
    }
    return searchParams.get('number') !== contactPhoneNumber;
  }, [
    voiceActions.contact?.phoneNumber,
    voiceActions.contact?.id,
    callState.userLine?.lineNumber,
    voiceActions.callContext?.origin_number,
    currentLine.phoneNumber,
    pathname,
    searchStr,
  ]);

  // Power Dialer end session callback for PiP (safe — may be null outside
  // provider). Note: this provider is an *ancestor* of ActivePowerDialerProvider
  // in the route tree, so this read currently always yields null. Pre-existing
  // dead code; tracked separately from this PR.
  const pdState = useActivePowerDialerSafe();

  // Auto Picture-in-Picture for calls
  const { openPipWindow, hidePipWindow, isPipWindowOpen, portalContent } = useCallPictureInPicture({
    callStatus: voiceActions.callStatus,
    callId: voiceActions.callId,
    contact: voiceActions.contact,
    isMuted: voiceActions.isMuted,
    duration,
    direction: voiceActions.direction,
    extra: callState.extra,
    userLine: callState.userLine || defaultUserLine,
    onToggleMute: voiceActions.toggleMute,
    onAnswer: answerCall,
    onHangup: hangupCall,
    onDecline: rejectCall,
    onContactClick: handleNavigateToCall,
    microphoneDeviceId: selectedMicrophone,
    hasSecondIncomingCall: callState.hasSecondIncomingCall,
    declineState,
    enabled: true,
    onEndSession: pdState?.activeSessionId ? pdState.endSession : undefined,
  });

  const contextValue: AppCallActionsContextType = useMemo(
    () => ({
      invite: voiceActions.invite,
      answerCall,
      rejectCall,
      hangupCall,
      call,
      updateContact: voiceActions.updateContact,

      callContext: voiceActions.callContext,
      contact: voiceActions.contact,
      userLine: voiceActions.userLine,
      callStatus: voiceActions.callStatus,
      callId: voiceActions.callId,
      from: voiceActions.from,
      direction: voiceActions.direction,
      isMuted: voiceActions.isMuted,
      isEarmuffed: voiceActions.isEarmuffed,
      toggleMute: voiceActions.toggleMute,
      toggleEarmuff: voiceActions.toggleEarmuff,
      sendDtmf,
      transferCall: voiceActions.transferCall,
      transferCallFailed: voiceActions.transferCallFailed,
      isRingingInbound,
      declineState,
      canNavigateToCall,
      navigateToCall: handleNavigateToCall,
      isNavigatingToCall,
      openPipWindow,
      hidePipWindow,
      isPipWindowOpen,
      duration,
    }),
    [
      voiceActions.contact,
      voiceActions.invite,
      voiceActions.callStatus,
      voiceActions.callContext,
      voiceActions.userLine,
      voiceActions.callId,
      voiceActions.from,
      voiceActions.direction,
      voiceActions.isMuted,
      voiceActions.isEarmuffed,
      voiceActions.toggleMute,
      voiceActions.toggleEarmuff,
      sendDtmf,
      voiceActions.transferCall,
      voiceActions.transferCallFailed,
      voiceActions.updateContact,
      answerCall,
      rejectCall,
      hangupCall,
      call,
      isRingingInbound,
      declineState,
      canNavigateToCall,
      handleNavigateToCall,
      isNavigatingToCall,
      openPipWindow,
      hidePipWindow,
      isPipWindowOpen,
      duration,
    ],
  );

  return (
    <AppCallActionsContext.Provider value={contextValue}>
      {children}
      {portalContent}
      <MicrophonePermissionModal
        isOpen={showMicPermissionModal}
        onClose={() => setShowMicPermissionModal(false)}
      />
    </AppCallActionsContext.Provider>
  );
};
